import { api, audioAssetPath } from "@/api/client";
import type { AudioManifest, Paper } from "@/api/types";

import {
  audioStageForPaper,
  manifestFailures,
  prefetchSegmentsFor,
  type ListenMode,
  type ListenStage,
} from "./audioPolicy";

export const AUDIO_PREFETCH_WINDOW = 6;
export const AUDIO_DOWNLOAD_CONCURRENCY = 2;

export interface AudioPrefetchApi {
  audioManifest: (paperId: number, stage: ListenStage) => Promise<AudioManifest>;
  prepareAudio: (paperId: number, stage: ListenStage, priority?: number) => Promise<unknown>;
  audioBlob: (path: string) => Promise<Blob>;
}

export interface AudioPrefetchResult {
  papers: number;
  downloads: number;
  pending: number;
  terminal: number;
}

async function runBounded(tasks: Array<() => Promise<void>>, concurrency: number): Promise<void> {
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < tasks.length) {
      const task = tasks[next];
      next += 1;
      if (task) await task();
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, tasks.length) }, worker));
}

export async function prefetchAudioWindow({
  enabled,
  papers,
  modes,
  client = api,
}: {
  enabled: boolean;
  papers: Paper[];
  modes: Record<ListenStage, ListenMode>;
  client?: AudioPrefetchApi;
}): Promise<AudioPrefetchResult> {
  if (!enabled) return { papers: 0, downloads: 0, pending: 0, terminal: 0 };
  const windowPapers = papers.slice(0, AUDIO_PREFETCH_WINDOW);
  const paths = new Set<string>();
  let pending = 0;
  let terminal = 0;

  await Promise.all(windowPapers.map(async (paper, index) => {
    const stage = audioStageForPaper(paper);
    try {
      const manifest = await client.audioManifest(paper.id, stage);
      const failure = manifestFailures(manifest, stage, modes[stage]);
      if (failure.audio || failure.summary) {
        terminal += 1;
        return;
      }
      const segments = prefetchSegmentsFor(manifest, stage, modes[stage]);
      if (!segments.length) {
        pending += 1;
        await client.prepareAudio(paper.id, stage, Math.max(45, 50 - index));
        return;
      }
      for (const segment of segments) {
        const path = audioAssetPath(segment);
        if (path) paths.add(path);
      }
    } catch {
      pending += 1;
    }
  }));

  let downloads = 0;
  await runBounded([...paths].map((path) => async () => {
    try {
      await client.audioBlob(path);
      downloads += 1;
    } catch {
      pending += 1;
    }
  }), AUDIO_DOWNLOAD_CONCURRENCY);

  return { papers: windowPapers.length, downloads, pending, terminal };
}
